<template>
	<div class="turnstile-widget" :class="{ 'turnstile-widget--error': !!errorMessage }">
		<div ref="host"></div>
		<p v-if="errorMessage" class="help is-danger">
			{{ errorMessage }}
		</p>
	</div>
</template>

<script>
	/*
	 * Cloudflare Turnstile. Same widget EAE uses on its own contact form.
	 *
	 * The token this emits is worthless on its own - it only means something once the API
	 * has posted it to Cloudflare's siteverify endpoint. See MemberController.AddNewMember.
	 */

	const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

	// One load for the whole app, however many widgets end up on the page.
	let scriptPromise = null;

	function loadTurnstile() {
		if (window.turnstile) {
			return Promise.resolve(window.turnstile);
		}
		if (scriptPromise) {
			return scriptPromise;
		}

		scriptPromise = new Promise((resolve, reject) => {
			const script = document.createElement('script');
			script.src = SCRIPT_URL;
			script.async = true;
			script.defer = true;
			script.onload = () => {
				if (window.turnstile) {
					resolve(window.turnstile);
				} else {
					reject(new Error('Turnstile script loaded but window.turnstile is missing'));
				}
			};
			script.onerror = () => {
				// Let a later mount try again rather than caching the failure forever.
				scriptPromise = null;
				reject(new Error('Turnstile script could not be loaded'));
			};
			document.head.appendChild(script);
		});

		return scriptPromise;
	}

	export default {
		name: 'TurnstileWidget',
		props: {
			// v-model. The token, or '' when there is not a valid one.
			value: {
				type: String,
				default: ''
			},
			// Public by design - it ships in the bundle and is visible in the page source.
			sitekey: {
				type: String,
				default: process.env.VUE_APP_TURNSTILE_SITEKEY
			},
			// 'tr' | 'en'. Falls back to the app language, like PhoneInput does.
			locale: {
				type: String,
				default: null
			},
			theme: {
				type: String,
				default: 'light'
			}
		},
		data() {
			return {
				widgetId: null,
				errorMessage: ''
			};
		},
		computed: {
			resolvedLocale() {
				if (this.locale) {
					return this.locale === 'tr' ? 'tr' : 'en';
				}
				const lang = window.localStorage.getItem('lang') || 'tr-TR';
				return lang.toLowerCase().startsWith('tr') ? 'tr' : 'en';
			},
			messages() {
				return this.resolvedLocale === 'tr'
					? {
						unavailable: 'Güvenlik doğrulaması yüklenemedi. Sayfayı yenileyip tekrar deneyin.',
						failed: 'Güvenlik doğrulaması başarısız oldu. Lütfen tekrar deneyin.',
						missingKey: 'Güvenlik doğrulaması yapılandırılmamış.'
					}
					: {
						unavailable: 'The security check could not be loaded. Please refresh the page and try again.',
						failed: 'The security check failed. Please try again.',
						missingKey: 'The security check is not configured.'
					};
			}
		},
		watch: {
			resolvedLocale() {
				this.rerender();
			}
		},
		mounted() {
			this.render();
		},
		beforeDestroy() {
			this.removeWidget();
		},
		methods: {
			render() {
				if (!this.sitekey) {
					// Missing VUE_APP_TURNSTILE_SITEKEY - fail loudly rather than silently
					// letting registrations through with no check at all.
					this.errorMessage = this.messages.missingKey;
					this.$emit('unavailable');
					return;
				}

				loadTurnstile()
					.then((turnstile) => {
						if (this._isDestroyed || !this.$refs.host) {
							return;
						}
						this.errorMessage = '';
						this.widgetId = turnstile.render(this.$refs.host, {
							sitekey: this.sitekey,
							language: this.resolvedLocale,
							theme: this.theme,
							callback: this.onToken,
							'expired-callback': this.onExpired,
							'timeout-callback': this.onExpired,
							'error-callback': this.onError
						});
					})
					.catch(() => {
						this.errorMessage = this.messages.unavailable;
						this.$emit('unavailable');
					});
			},

			rerender() {
				this.removeWidget();
				this.setToken('');
				this.render();
			},

			removeWidget() {
				if (this.widgetId !== null && window.turnstile) {
					try {
						window.turnstile.remove(this.widgetId);
					} catch (err) { /* already gone */ }
				}
				this.widgetId = null;
			},

			onToken(token) {
				this.errorMessage = '';
				this.setToken(token);
			},

			onExpired() {
				// Tokens are single-use and short-lived; ask for a fresh one silently.
				this.setToken('');
				this.reset();
			},

			onError() {
				this.errorMessage = this.messages.failed;
				this.setToken('');
			},

			setToken(token) {
				this.$emit('input', token || '');
			},

			/** Call after a rejected submit: the old token is spent and cannot be reused. */
			reset() {
				if (this.widgetId !== null && window.turnstile) {
					try {
						window.turnstile.reset(this.widgetId);
					} catch (err) { /* widget is gone; nothing to reset */ }
				}
				this.setToken('');
			},

			/** Shows the "please complete the check" message. Called by the form on submit. */
			showRequired(message) {
				this.errorMessage = message || this.messages.failed;
			},

			clearError() {
				this.errorMessage = '';
			}
		}
	};
</script>

<style lang="scss">
	.turnstile-widget {
		margin-bottom: 0.75rem;
	}

	.turnstile-widget--error iframe {
		outline: 1px solid #c2161c;
	}
</style>
